import { useEffect } from 'react';
import { CLINIC_SCHEMA, DEFAULT_OG_IMAGE, ORGANIZATION_ID, SITE_NAME, SITE_URL, WEBSITE_ID } from '../config/seo';
import { API_BASE_URL } from '../config/runtime';
import { buildHomePageSchemaGraph } from '../config/homeSchema';
import { buildArticleSchemaGraph, removeHomepageSchemaScripts } from '../utils/articleSchema';
import { useSettings } from '../contexts/SettingsContext';

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageAlt?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  ogUrl?: string;
  robots?: string;
  jsonLd?: string;
}

export const isCustomSchemaActive = () => {
  if (typeof document === 'undefined') return false;
  return (
    document.head.getAttribute('data-custom-seo-active') === 'true' ||
    !!document.querySelector('script[data-custom-seo-jsonld="true"]')
  );
};

export const removeDefaultSchemas = () => {
  if (typeof document === 'undefined') return;
  const ids = [
    'homepage-schema-graph',
    'homepage-faq-schema',
    'local-business-schema',
    'website-schema',
    'blog-schema',
    'page-schema',
    'breadcrumb-schema',
    'faq-schema',
    'gallery-schema',
    'article-schema-graph',
  ];
  ids.forEach((id) => document.getElementById(id)?.remove());
  document
    .querySelectorAll(
      'script[id^="medical-service-schema-"], script[id^="doctor-schema-"], script[id^="article-schema-"], script[id^="static-page-schema-"]'
    )
    .forEach((el) => el.remove());
};

const applyCustomJsonLd = (rawJsonLd: string) => {
  if (!rawJsonLd) return;
  try {
    const parsed = JSON.parse(rawJsonLd);
    document.head.setAttribute('data-custom-seo-active', 'true');
    removeDefaultSchemas();
    document.querySelectorAll('script[data-custom-seo-jsonld="true"]').forEach((el) => el.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-custom-seo-jsonld', 'true');
    script.text = JSON.stringify(parsed);
    document.head.appendChild(script);
  } catch (e) {
    console.error('Invalid custom JSON-LD schema', e);
  }
};

export const useSEOMeta = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogTitle,
  ogDescription,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogUrl,
  robots,
  jsonLd,
}: SEOMetaProps) => {
  useEffect(() => {
    const cleanTitle = title.replace(/\s*\|\s*Dynamic\s+Dental(\s+Clinic)?\s*$/i, '');
    const suffix = ' | Dynamic Dental';
    const finalTitle = cleanTitle.length + suffix.length <= 60
      ? `${cleanTitle}${suffix}`
      : cleanTitle;
    document.title = finalTitle;

    // Batch DOM updates using a DocumentFragment for performance
    
    const setOrCreateMeta = (name: string, content: string, property?: string) => {
      const attr = property ? 'property' : 'name';
      const attrValue = property || name;
      let element = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setOrCreateLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Fetch custom SEO override for the current route
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/admin') && currentPath !== '/login') {
      fetch(`${API_BASE_URL}/seo/by-url?url=${encodeURIComponent(currentPath)}`)
        .then(response => {
          if (response.ok) return response.json();
          throw new Error('No override');
        })
        .then(override => {
          if (window.location.pathname !== currentPath) return;
          if (override) {
            const cleanTitle = (override.metaTitle || title).replace(/\s*\|\s*Dynamic\s+Dental(\s+Clinic)?\s*$/i, '');
            const suffix = ' | Dynamic Dental';
            const finalTitle = cleanTitle.length + suffix.length <= 60
              ? `${cleanTitle}${suffix}`
              : cleanTitle;
            document.title = finalTitle;

            const finalDesc = override.metaDescription || description;
            const finalKeywords = override.keywords || keywords;
            const finalRobots = override.robots || 'index, follow';
            const finalOgTitle = override.ogTitle || override.metaTitle || ogTitle || title;
            const finalOgDesc = override.ogDescription || override.metaDescription || ogDescription || description;
            const finalOgImage = override.ogImage || ogImage;

            requestAnimationFrame(() => {
              setOrCreateMeta('description', finalDesc);
              if (finalKeywords) setOrCreateMeta('keywords', finalKeywords);
              setOrCreateMeta('robots', finalRobots);
              setOrCreateMeta('googlebot', finalRobots);
              setOrCreateMeta('og:title', finalOgTitle, 'property');
              setOrCreateMeta('og:description', finalOgDesc, 'property');
              if (finalOgImage) {
                setOrCreateMeta('og:image', finalOgImage, 'property');
              }
              if (override.canonical) {
                setOrCreateLink('canonical', override.canonical);
              }
              
              if (override.jsonLd) {
                applyCustomJsonLd(override.jsonLd);
              }
            });
          }
        })
        .catch(() => {
          // No override, falls back to default values applied below
        });
    }

    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      let finalCanonical = canonicalUrl;
      if (finalCanonical && !finalCanonical.endsWith('/')) {
        finalCanonical = `${finalCanonical}/`;
      }

      let finalOgUrl = ogUrl || finalCanonical || SITE_URL;
      if (finalOgUrl && !finalOgUrl.endsWith('/')) {
        finalOgUrl = `${finalOgUrl}/`;
      }

      setOrCreateMeta('description', description);
      if (keywords) setOrCreateMeta('keywords', keywords);
      setOrCreateMeta(
        'robots',
        robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
      setOrCreateMeta(
        'googlebot',
        robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
      setOrCreateMeta('og:title', ogTitle || title, 'property');
      setOrCreateMeta('og:description', ogDescription || description, 'property');
      setOrCreateMeta('og:type', 'website', 'property');
      setOrCreateMeta('og:url', finalOgUrl, 'property');
      if (ogImage) {
        setOrCreateMeta('og:image', ogImage, 'property');
        if (ogImageAlt) setOrCreateMeta('og:image:alt', ogImageAlt, 'property');
        if (ogImageWidth) setOrCreateMeta('og:image:width', ogImageWidth, 'property');
        if (ogImageHeight) setOrCreateMeta('og:image:height', ogImageHeight, 'property');
      }
      setOrCreateMeta('twitter:card', 'summary_large_image', 'name');
      setOrCreateMeta('twitter:title', ogTitle || title, 'name');
      setOrCreateMeta('twitter:description', ogDescription || description, 'name');
      if (ogImage) {
        setOrCreateMeta('twitter:image', ogImage, 'name');
        if (ogImageAlt) setOrCreateMeta('twitter:image:alt', ogImageAlt, 'name');
      }
      if (finalCanonical) setOrCreateLink('canonical', finalCanonical);

      // Inject custom schema JSON-LD if provided in props
      if (jsonLd) {
        applyCustomJsonLd(jsonLd);
      }
    });

    return () => {
      document.title = 'Best Dentist in Lalitpur, Nepal | Dynamic Dental Clinic';
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogTitle, ogDescription, ogImageAlt, ogImageWidth, ogImageHeight, ogUrl, robots, jsonLd]);
};

interface FAQItem {
  question: string;
  answer: string;
}

export const FAQSchema: React.FC<{ items: FAQItem[] }> = ({ items }) => {
  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('faq-schema')?.remove();
      return;
    }
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': items.map(item => ({
        '@type': 'Question',
        'name': item.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': item.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('faq-schema');
      if (existing) existing.remove();
    };
  }, [items]);

  return null;
};

interface LocalBusinessProps {
  name?: string;
  description?: string;
  url?: string;
  image?: string;
  telephone?: string;
  email?: string;
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  openingHours?: string[];
  priceRange?: string;
  rating?: {
    ratingValue: number;
    reviewCount: number;
  };
  services?: Array<string | { name?: string; title?: string }>;
}

export const LocalBusinessSchema: React.FC<LocalBusinessProps> = ({
  name,
  description = CLINIC_SCHEMA.description,
  url = CLINIC_SCHEMA.url,
  image = CLINIC_SCHEMA.image,
  telephone,
  email,
  address,
  geo = CLINIC_SCHEMA.geo,
  openingHours,
  priceRange = CLINIC_SCHEMA.priceRange,
  rating,
  services,
}) => {
  const { settings } = useSettings();

  const finalName = name || settings.clinicName || CLINIC_SCHEMA.name;
  const finalTelephone = telephone || settings.phone || CLINIC_SCHEMA.telephone;
  const finalEmail = email || settings.email || CLINIC_SCHEMA.email;
  const finalAddress = {
    streetAddress: address?.streetAddress || settings.address || CLINIC_SCHEMA.address.streetAddress,
    addressLocality: address?.addressLocality || CLINIC_SCHEMA.address.addressLocality,
    addressRegion: address?.addressRegion || CLINIC_SCHEMA.address.addressRegion,
    postalCode: address?.postalCode || CLINIC_SCHEMA.address.postalCode,
    addressCountry: address?.addressCountry || CLINIC_SCHEMA.address.addressCountry,
  };

  const rawSocials = settings
    ? [
        settings.facebookLink,
        settings.instagramLink,
        settings.youtubeLink,
        settings.linkedinLink,
        ...CLINIC_SCHEMA.sameAs,
      ].filter(Boolean)
    : [...CLINIC_SCHEMA.sameAs];

  const sameAs = Array.from(new Set(rawSocials));

  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('local-business-schema')?.remove();
      return;
    }

    const hoursSpec = openingHours
      ? openingHours.map((hours) => {
          const [days, time] = hours.split(' ');
          const [open, close] = time.split('-');
          const dayList = days.split('-');
          return {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: dayList.map((d) => `https://schema.org/${d}`),
            opens: open,
            closes: close,
          };
        })
      : CLINIC_SCHEMA.openingHours.map((spec) => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: spec.dayOfWeek,
          opens: spec.opens,
          closes: spec.closes,
        }));

    const offerItems = services && services.length > 0
      ? services.map((s) => {
          const serviceName = typeof s === 'string' ? s : s.title || s.name || '';
          return { '@type': 'Offer', itemOffered: { '@type': 'Service', name: serviceName } };
        })
      : [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teeth Cleaning & Scaling' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dental Implants' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Orthodontics & Braces' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Root Canal Treatment' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teeth Whitening' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Emergency Dental Care' } },
        ];

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'DentalClinic',
      '@id': ORGANIZATION_ID,
      name: finalName,
      alternateName: CLINIC_SCHEMA.alternateName,
      description,
      url,
      image,
      telephone: finalTelephone,
      email: finalEmail,
      address: {
        '@type': 'PostalAddress',
        streetAddress: finalAddress.streetAddress,
        addressLocality: finalAddress.addressLocality,
        addressRegion: finalAddress.addressRegion,
        postalCode: finalAddress.postalCode,
        addressCountry: finalAddress.addressCountry,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: geo.latitude,
        longitude: geo.longitude,
      },
      openingHoursSpecification: hoursSpec,
      priceRange,
      sameAs,
      areaServed: [
        { '@type': 'City', name: 'Lalitpur' },
        { '@type': 'City', name: 'Kathmandu' },
        { '@type': 'City', name: 'Patan' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Dental Services',
        itemListElement: offerItems,
      },
      ...(rating && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating.ratingValue,
          reviewCount: rating.reviewCount,
          bestRating: '5',
          worstRating: '1',
        },
      }),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'local-business-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('local-business-schema');
      if (existing) existing.remove();
    };
  }, [
    finalName,
    finalTelephone,
    finalEmail,
    finalAddress.streetAddress,
    finalAddress.addressCountry,
    finalAddress.addressLocality,
    finalAddress.addressRegion,
    finalAddress.postalCode,
    description,
    geo.latitude,
    geo.longitude,
    image,
    openingHours,
    priceRange,
    rating,
    services,
    sameAs,
    url,
  ]);

  return null;
};

export const MedicalServiceSchema: React.FC<{
  name: string;
  description: string;
  serviceUrl: string;
  image?: string;
  serviceType?: string;
  areaServed?: string[];
}> = ({
  name,
  description,
  serviceUrl,
  image,
  serviceType,
  areaServed = ['Lalitpur', 'Kathmandu', 'Patan'],
}) => {
  const { settings } = useSettings();

  useEffect(() => {
    const scriptId = `medical-service-schema-${name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`;
    if (isCustomSchemaActive()) {
      document.getElementById(scriptId)?.remove();
      return;
    }

    const finalUrl = serviceUrl.startsWith('http') ? serviceUrl : `${SITE_URL}${serviceUrl.startsWith('/') ? '' : '/'}${serviceUrl}`;
    const finalImage = image || DEFAULT_OG_IMAGE;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'MedicalService',
      'name': name,
      'description': description,
      'url': finalUrl,
      'image': finalImage,
      'serviceType': serviceType || 'Dentistry',
      'category': 'Dental Care',
      'provider': {
        '@type': 'DentalClinic',
        '@id': ORGANIZATION_ID,
        'name': settings.clinicName || SITE_NAME,
        'url': SITE_URL,
        'telephone': settings.phone || CLINIC_SCHEMA.telephone,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': settings.address || CLINIC_SCHEMA.address.streetAddress,
          'addressLocality': 'Lalitpur',
          'addressRegion': 'Bagmati',
          'postalCode': '44700',
          'addressCountry': 'NP',
        },
      },
      'areaServed': areaServed.map((city: string) => ({
        '@type': 'City',
        'name': city,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [name, description, serviceUrl, image, serviceType, areaServed, settings]);

  return null;
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

export const BreadcrumbSchema: React.FC<{ items: BreadcrumbItem[] }> = ({ items }) => {
  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('breadcrumb-schema')?.remove();
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': items.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url.startsWith('/') ? '' : '/'}${item.url}`
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'breadcrumb-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('breadcrumb-schema');
      if (existing) existing.remove();
    };
  }, [items]);

  return null;
};

export const WebSiteSchema: React.FC = () => {
  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('website-schema')?.remove();
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: SITE_URL,
      name: SITE_NAME,
      description: CLINIC_SCHEMA.description,
      publisher: { '@id': ORGANIZATION_ID },
      inLanguage: 'en',
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'website-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('website-schema');
      if (existing) existing.remove();
    };
  }, []);

  return null;
};

export const HomePageSchema: React.FC<{
  services?: Array<{ title: string }>;
  faqs?: Array<{ question: string; answer: string }>;
  articles?: Array<{ title: string; slug: string }>;
}> = ({ services, faqs, articles }) => {
  const { settings } = useSettings();

  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('homepage-schema-graph')?.remove();
      document.getElementById('homepage-faq-schema')?.remove();
      return;
    }

    document.querySelectorAll('head script[type="application/ld+json"]').forEach((script) => {
      if (!script.id && script.getAttribute('data-custom-seo-jsonld') !== 'true') {
        script.remove();
      }
    });

    let graphScript = document.getElementById('homepage-schema-graph') as HTMLScriptElement | null;
    if (!graphScript) {
      graphScript = document.createElement('script');
      graphScript.type = 'application/ld+json';
      graphScript.id = 'homepage-schema-graph';
      document.head.appendChild(graphScript);
    }
    graphScript.textContent = JSON.stringify(buildHomePageSchemaGraph(settings, services, faqs, articles));

    // Legacy standalone FAQ block — remove when present (FAQ now lives in @graph)
    document.getElementById('homepage-faq-schema')?.remove();

    return () => {
      document.getElementById('homepage-schema-graph')?.remove();
    };
  }, [settings, services, faqs, articles]);

  return null;
};

export const BlogSchema: React.FC<{
  posts?: Array<{ title: string; url: string; datePublished?: string }>;
}> = ({ posts = [] }) => {
  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('blog-schema')?.remove();
      return;
    }

    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${SITE_URL}/articles#blog`,
      name: 'Dynamic Dental Health Blog',
      description:
        'Expert dental health articles from Dynamic Dental in Lalitpur, Nepal.',
      url: `${SITE_URL}/articles`,
      publisher: { '@id': ORGANIZATION_ID },
      inLanguage: 'en',
    };

    if (posts.length > 0) {
      schema.blogPost = posts.map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        url: post.url.startsWith('http') ? post.url : `${SITE_URL}${post.url}`,
        datePublished: post.datePublished,
      }));
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('blog-schema');
      if (existing) existing.remove();
    };
  }, [posts]);

  return null;
};

export const ArticleSchema: React.FC<{
  title: string;
  slug?: string;
  id?: string;
  description?: string;
  content?: string;
  excerpt?: string;
  image?: string;
  category?: string;
  tags?: string[];
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  url?: string;
}> = ({
  title,
  slug,
  id,
  description,
  content,
  excerpt,
  image,
  category,
  tags,
  datePublished,
  dateModified,
  authorName,
  url,
}) => {
  useEffect(() => {
    if (isCustomSchemaActive()) {
      document.getElementById('page-schema')?.remove();
      document.getElementById('article-schema')?.remove();
      return;
    }

    removeHomepageSchemaScripts();

    const schema = buildArticleSchemaGraph({
      title,
      slug,
      id,
      description,
      content,
      excerpt,
      imageUrl: image,
      category,
      tags,
      author: authorName,
      publishedAt: datePublished,
      createdAt: datePublished,
      updatedAt: dateModified,
    });

    let script = document.getElementById('page-schema') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'page-schema';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    const legacy = document.getElementById('article-schema');
    if (legacy) legacy.remove();

    return () => {
      const existing = document.getElementById('page-schema');
      if (existing) existing.remove();
    };
  }, [title, slug, id, description, content, excerpt, image, category, tags, datePublished, dateModified, authorName, url]);

  return null;
};

export const DoctorSchema: React.FC<{
  name: string;
  role: string;
  slug: string;
  qualifications: string[];
  specialties: string[];
  nmcNumber?: string;
}> = ({ name, role, slug, qualifications, specialties, nmcNumber }) => {
  useEffect(() => {
    const scriptId = `doctor-schema-${slug}`;
    if (isCustomSchemaActive()) {
      document.getElementById(scriptId)?.remove();
      return;
    }

    const docUrl = `${SITE_URL}/doctors/${slug}/`;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Physician',
      '@id': `${docUrl}#physician`,
      name: name,
      image: `${SITE_URL}/logo192.png`,
      url: docUrl,
      jobTitle: role,
      medicalSpecialty: specialties.map((s) => s.trim()),
      hasCredential: qualifications.map((q) => ({
        '@type': 'EducationalOccupationalCredential',
        name: q,
      })),
      worksFor: {
        '@type': 'DentalClinic',
        '@id': ORGANIZATION_ID,
        name: SITE_NAME,
        url: SITE_URL,
      },
    } as any;

    if (nmcNumber) {
      schema.identifier = {
        '@type': 'PropertyValue',
        name: 'Nepal Medical Council License',
        value: nmcNumber,
      };
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [name, role, slug, qualifications, specialties, nmcNumber]);

  return null;
};
